import { ChevronDown } from 'lucide-react';
import { useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';

import { Flex } from '@/components/base';
import Typo from '@/components/Typography/Typo';
import useColor from '@/hooks/domain/useColor';
import { cn } from '@/lib/cn';
import { radius } from '@/styles/sizes';
import { animation, ellipsisText } from '@/styles/style';

import WorkspaceDropdown from './WorkspaceDropdown';

type Props = {
  shrinked: boolean;
  enabled: boolean;
  workspaceName: string;
};

const WorkspaceSelector = (props: Props) => {
  const navigate = useNavigate();
  const color = useColor();
  const [modalStatus, setModalStatus] = useState(false);
  const workspaceRef = useRef<HTMLDivElement>(null);
  return (
    <Flex a="center" f={1} g={props.shrinked ? 0 : 4} className="min-w-0">
      <div
        className="shrink-0"
        style={{ width: 24, height: 24, cursor: 'pointer' }}
        onClick={() => navigate('/')}
      >
        <img
          src="/assets/logo-rebranding-syncly.svg"
          width="100%"
          height="100%"
          alt="Syncly"
        />
      </div>
      <SelectedWorkspaceWrapper
        ref={workspaceRef}
        onClick={() => {
          if (!props.shrinked && props.enabled) {
            setModalStatus(true);
          }
        }}
        f={1}
        className={cn(props.shrinked && 'is-shrinked')}
      >
        <Typo
          style={{ userSelect: 'none' }}
          weight="sb"
          type="label2"
          color={color.gray800}
          className="workspace-name"
        >
          {props.workspaceName}
        </Typo>
        <ChevronDown className="workspace-chevron w-3.5 h-3.5" />
      </SelectedWorkspaceWrapper>
      <WorkspaceDropdown
        status={modalStatus}
        close={() => setModalStatus(false)}
        animations={{
          in: animation.growDownIn,
          out: animation.growDownOut,
        }}
        modalWidth="260px"
        triggerEl={workspaceRef.current}
      >
        <WorkspaceDropdown.WorkspaceList />
        <WorkspaceDropdown.CreateWorkspace />
      </WorkspaceDropdown>
    </Flex>
  );
};

const SelectedWorkspaceWrapper = styled(Flex).attrs({
  a: 'center',
  g: 6,
})`
  width: 100%;
  min-width: 0;
  padding: 6px 8px;
  cursor: pointer;
  font-size: 13px;
  border-radius: ${radius.token.lg}px;
  overflow: hidden;
  transform-origin: left center;
  transition:
    opacity 0.18s ease-out,
    transform 0.18s ease-out,
    max-width 0.18s ease-out,
    padding 0.18s ease-out,
    margin 0.18s ease-out,
    background-color 0.18s ease-out;

  &.is-shrinked {
    max-width: 0;
    opacity: 0;
    transform: translateX(-8px);
    pointer-events: none;
    padding: 0;
    margin-left: 0;
  }

  &:hover {
    background-color: ${(props) => props.theme.colors.sidebar_accent};
  }

  .workspace-name {
    max-width: 100%;
    text-align: left;
    ${ellipsisText};
  }

  .workspace-chevron {
    flex-shrink: 0;
  }
`;

export default WorkspaceSelector;
